import { lazy, Suspense, forwardRef, ComponentType } from "react";
import { LucideProps } from "lucide-react";

/**
 * Curated icon map — only icons selectable in admin (social + menu).
 * Avoids importing the full 38 KiB dynamicIconImports map from lucide-react.
 * Each entry is a lazy import (~0 cost until actually rendered).
 */
const iconMap: Record<string, () => Promise<{ default: ComponentType<LucideProps> }>> = {
  // Social platforms
  instagram: () => import("lucide-react/dist/esm/icons/instagram"),
  facebook: () => import("lucide-react/dist/esm/icons/facebook"),
  linkedin: () => import("lucide-react/dist/esm/icons/linkedin"),
  twitter: () => import("lucide-react/dist/esm/icons/twitter"),
  youtube: () => import("lucide-react/dist/esm/icons/youtube"),
  music: () => import("lucide-react/dist/esm/icons/music"),
  globe: () => import("lucide-react/dist/esm/icons/globe"),
  link: () => import("lucide-react/dist/esm/icons/link"),
  "message-circle": () => import("lucide-react/dist/esm/icons/message-circle"),
  send: () => import("lucide-react/dist/esm/icons/send"),
  rss: () => import("lucide-react/dist/esm/icons/rss"),
  "share-2": () => import("lucide-react/dist/esm/icons/share-2"),
  "at-sign": () => import("lucide-react/dist/esm/icons/at-sign"),
  hash: () => import("lucide-react/dist/esm/icons/hash"),
  // Menu card icons
  utensils: () => import("lucide-react/dist/esm/icons/utensils"),
  wine: () => import("lucide-react/dist/esm/icons/wine"),
  coffee: () => import("lucide-react/dist/esm/icons/coffee"),
  beer: () => import("lucide-react/dist/esm/icons/beer"),
  "glass-water": () => import("lucide-react/dist/esm/icons/glass-water"),
  cake: () => import("lucide-react/dist/esm/icons/cake"),
  croissant: () => import("lucide-react/dist/esm/icons/croissant"),
  grape: () => import("lucide-react/dist/esm/icons/grape"),
  salad: () => import("lucide-react/dist/esm/icons/salad"),
  sandwich: () => import("lucide-react/dist/esm/icons/sandwich"),
  soup: () => import("lucide-react/dist/esm/icons/soup"),
  "ice-cream-cone": () => import("lucide-react/dist/esm/icons/ice-cream-cone"),
  pizza: () => import("lucide-react/dist/esm/icons/pizza"),
  "egg-fried": () => import("lucide-react/dist/esm/icons/egg-fried"),
  cherry: () => import("lucide-react/dist/esm/icons/cherry"),
  apple: () => import("lucide-react/dist/esm/icons/apple"),
  citrus: () => import("lucide-react/dist/esm/icons/citrus"),
  leaf: () => import("lucide-react/dist/esm/icons/leaf"),
  sun: () => import("lucide-react/dist/esm/icons/sun"),
  sparkles: () => import("lucide-react/dist/esm/icons/sparkles"),
  star: () => import("lucide-react/dist/esm/icons/star"),
  heart: () => import("lucide-react/dist/esm/icons/heart"),
  calendar: () => import("lucide-react/dist/esm/icons/calendar"),
  award: () => import("lucide-react/dist/esm/icons/award"),
  crown: () => import("lucide-react/dist/esm/icons/crown"),
  gem: () => import("lucide-react/dist/esm/icons/gem"),
  flame: () => import("lucide-react/dist/esm/icons/flame"),
  martini: () => import("lucide-react/dist/esm/icons/martini"),
  // Common UI
  "map-pin": () => import("lucide-react/dist/esm/icons/map-pin"),
  mail: () => import("lucide-react/dist/esm/icons/mail"),
  phone: () => import("lucide-react/dist/esm/icons/phone"),
  "external-link": () => import("lucide-react/dist/esm/icons/external-link"),
  // Fallback
  "file-text": () => import("lucide-react/dist/esm/icons/file-text"),
};

interface DynamicIconProps extends Omit<LucideProps, "ref"> {
  name: string;
}

const DynamicIcon = forwardRef<SVGSVGElement, DynamicIconProps>(({ name, ...props }, ref) => {
  const key = name.toLowerCase();
  const loader = iconMap[key] || iconMap["file-text"];
  const LucideIcon = lazy(loader);

  return (
    <Suspense fallback={<div style={{ width: (props.size as number) ?? 24, height: (props.size as number) ?? 24 }} />}>
      <LucideIcon ref={ref} {...props} />
    </Suspense>
  );
});

DynamicIcon.displayName = "DynamicIcon";

export default DynamicIcon;
